import { Box, Dialog, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WORKSPACES, sectionPath, visibleSections } from '@/app/workspaces';
import { useAuth } from '@/lib/auth/AuthContext';
import { canAny, type Permission } from '@/lib/auth/permissions';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PALETA POLECEŃ — przeniesiona z makiety
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Cytat z makiety, bo trafnie nazywa powód istnienia: „Nie ozdoba. Przy jedenastu ekranach
 * i kilkudziesięciu bytach szukanie klikaniem jest wolniejsze od pisania. Właściciel, który używa
 * produktu codziennie, przestaje korzystać z menu po tygodniu."
 *
 * CZEGO TA PALETA NIE ROBI — i to jest granica, nie brak:
 * prowadzi WYŁĄCZNIE do miejsc, które i tak są w nawigacji, i wyłącznie do tych, do których dana
 * osoba ma uprawnienia. Zero operacji zapisu, zero skrótów do narzędzi, których nie ma. Paleta,
 * która „umie" więcej niż produkt, jest obietnicą bez pokrycia — a przy poleceniach pisanych
 * z klawiatury użytkownik zorientuje się dopiero po naciśnięciu Enter.
 *
 * DOSTĘPNOŚĆ: `Dialog` daje pułapkę ogniska i obsługę Escape. MUI stawia `role="dialog"` na
 * warstwie Paper, więc nazwa dostępna MUSI iść w `slotProps.paper` — na samym `<Dialog>` ląduje
 * na elemencie bez roli i czytnik ekranu jej nie przeczyta. Ta pułapka kosztowała już jeden
 * defekt w launcherze produktów.
 */

export interface PozycjaPalety {
  tekst: string;
  cel: string;
  rodzaj: string;
}

const MAKS_WYNIKOW = 12;
const ZDARZENIE_OTWARCIA = 'paleta:otworz';

/**
 * Przycisk wyszukiwania w górnym pasku.
 *
 * Makieta trzyma wyszukiwanie NA WIDOKU, a nie tylko pod skrótem — i słusznie: skrót zna ten,
 * kto już wie, że istnieje. Widoczne „Ctrl K" na przycisku uczy o nim resztę.
 */
export function PrzyciskPalety() {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => window.dispatchEvent(new Event(ZDARZENIE_OTWARCIA))}
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: 1,
        // Wyszukiwarka jest JEDYNYM elastycznym elementem paska: kurczy się pierwsza,
        // żeby okruszki i chipy prawdy zostały czytelne, a pas nie zaczął przewijać się w poziomie.
        flex: '0 1 auto',
        minWidth: 0,
        maxWidth: 260,
        px: 1.5,
        py: 0.75,
        cursor: 'pointer',
        borderRadius: 'var(--r-pigulka)',
        border: '1px solid var(--linia)',
        background: 'var(--powierzchnia)',
        color: 'var(--tekst-slaby)',
        fontSize: 'var(--st-maly)',
        transition: 'border-color var(--czas-szybko), box-shadow var(--czas-szybko)',
        '&:hover': { borderColor: 'var(--linia-mocna)', boxShadow: 'var(--cien-1)' },
      }}
    >
      <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden focusable="false"
        style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 1.9, strokeLinecap: 'round' }}>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      {/* `nowrap`: bez tego etykieta łamała się na „Szukaj / ekranu…" i przycisk rósł w pionie,
          rozpychając cały pas górny. Kurczy się wielokropkiem, tak jak okruszki. */}
      <Box sx={{ flexGrow: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        Szukaj ekranu…
      </Box>
      <Box
        component="kbd"
        sx={{
          px: 0.75,
          py: '1px',
          borderRadius: 'var(--r-drobny)',
          background: 'var(--tlo-glebokie)',
          fontFamily: 'var(--krój-mono)',
          fontSize: 'var(--st-mikro)',
        }}
      >
        Ctrl K
      </Box>
    </Box>
  );
}

export function PaletaPolecen() {
  const [otwarta, setOtwarta] = useState(false);
  const [fraza, setFraza] = useState('');
  const [podswietlona, setPodswietlona] = useState(0);
  const navigate = useNavigate();
  const auth = useAuth();
  const wejscie = useRef<HTMLInputElement>(null);

  const isVisible = useCallback(
    (required?: readonly Permission[]) => canAny(auth.permissions, required),
    [auth.permissions],
  );

  /* Cele pochodzą z TEJ SAMEJ mapy co szyna — jedno źródło prawdy o tym, dokąd da się przejść. */
  const pozycje = useMemo<PozycjaPalety[]>(
    () =>
      WORKSPACES.flatMap((w) =>
        visibleSections(w, isVisible).map((s) => ({
          tekst: s.label,
          cel: sectionPath(w, s),
          rodzaj: w.label,
        })),
      ),
    [isVisible],
  );

  const pasujace = useMemo(() => {
    const f = fraza.trim().toLowerCase();
    const zbior = f
      ? pozycje.filter((p) => `${p.tekst} ${p.rodzaj}`.toLowerCase().includes(f))
      : pozycje;
    return zbior.slice(0, MAKS_WYNIKOW);
  }, [fraza, pozycje]);

  /* Ctrl+K / ⌘K — globalnie. Przycisk w górnym pasku otwiera to samo przez zdarzenie, zamiast
     przeciągać stan przez całą powłokę: paleta jest jedna i sama jest właścicielem swojego stanu. */
  useEffect(() => {
    const otworz = () => {
      setFraza('');
      setPodswietlona(0);
      setOtwarta(true);
    };
    const naKlawisz = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setFraza('');
        setPodswietlona(0);
        setOtwarta((b) => !b);
      }
    };
    window.addEventListener('keydown', naKlawisz);
    window.addEventListener(ZDARZENIE_OTWARCIA, otworz);
    return () => {
      window.removeEventListener('keydown', naKlawisz);
      window.removeEventListener(ZDARZENIE_OTWARCIA, otworz);
    };
  }, []);

  const przejdz = (cel: string) => {
    setOtwarta(false);
    navigate(cel);
  };

  const naKlawiszWPolu = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setPodswietlona((i) =>
        Math.max(0, Math.min(pasujace.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1))),
      );
      return;
    }
    if (e.key === 'Enter' && pasujace[podswietlona]) {
      e.preventDefault();
      przejdz(pasujace[podswietlona]!.cel);
    }
  };

  return (
    <Dialog
      open={otwarta}
      onClose={() => setOtwarta(false)}
      /* Makieta stawia paletę wysoko, nie na środku: oko szuka pola wyszukiwania u góry,
         a lista musi mieć miejsce, żeby rosnąć w dół bez przeskakiwania. */
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', pt: '14vh' } }}
      slotProps={{
        backdrop: { sx: { background: 'rgba(25, 25, 35, 0.5)', backdropFilter: 'blur(6px)' } },
        paper: {
          'aria-label': 'Paleta poleceń',
          sx: {
            width: 'min(620px, 92vw)',
            m: 0,
            borderRadius: 'var(--r-duzy)',
            boxShadow: 'var(--cien-3)',
            overflow: 'hidden',
            background: 'var(--powierzchnia)',
          },
        },
      }}
    >
      <Box
        component="input"
        ref={wejscie}
        autoFocus
        autoComplete="off"
        aria-label="Dokąd chcesz przejść?"
        placeholder="Dokąd chcesz przejść?"
        value={fraza}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setFraza(e.target.value);
          setPodswietlona(0);
        }}
        onKeyDown={naKlawiszWPolu}
        sx={{
          width: '100%',
          p: 3,
          border: 'none',
          background: 'transparent',
          fontFamily: 'var(--krój-marka)',
          fontSize: 'var(--st-duzy)',
          color: 'var(--tekst)',
          outline: 'none',
          '&::placeholder': { color: 'var(--tekst-slaby)' },
        }}
      />

      <Box
        role="listbox"
        aria-label="Wyniki"
        sx={{ maxHeight: '46vh', overflowY: 'auto', borderTop: '1px solid var(--linia)', p: 1 }}
      >
        {pasujace.length === 0 ? (
          <Box sx={{ px: 1.5, py: 1.4, fontSize: 'var(--st-maly)', color: 'var(--tekst-slaby)' }}>
            {`Nic nie pasuje do „${fraza}”`}
          </Box>
        ) : (
          pasujace.map((p, i) => (
            <Stack
              key={p.cel}
              component="button"
              role="option"
              aria-selected={i === podswietlona}
              type="button"
              direction="row"
              onMouseEnter={() => setPodswietlona(i)}
              onClick={() => przejdz(p.cel)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                px: 1.5,
                py: '11px',
                border: 'none',
                cursor: 'pointer',
                borderRadius: 'var(--r-maly)',
                fontSize: 'var(--st-maly)',
                textAlign: 'left',
                color: 'var(--tekst)',
                background: i === podswietlona ? 'rgba(247, 177, 11, 0.16)' : 'transparent',
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>{p.tekst}</Box>
              <Typography component="small" sx={{ fontSize: 'var(--st-drobny)', color: 'var(--tekst-slaby)' }}>
                {p.rodzaj}
              </Typography>
            </Stack>
          ))
        )}
      </Box>
    </Dialog>
  );
}
